import apiClient from '@/api/index.js'

const state = {
  inventory: [],
  inventoryItem: null,
  loading: false,
  error: null,
  stats: {
    totalProducts: 0,
    recentTransactions: 0,
    totalLocations: 0,
    alerts: 0,
    distribution: []
  },
  recentActivities: [],
  lowStockProducts: []
}

const getters = {
  allInventory: state => state.inventory,
  inventoryItemById: state => id => state.inventory.find(item => item.id === id),
  loading: state => state.loading,
  inventoryStats: state => state.stats,
  recentActivities: state => state.recentActivities,
  lowStockProducts: state => state.lowStockProducts
}

const actions = {
  async fetchInventory({ commit }) {
    console.log('Iniciando fetchInventory')
    commit('SET_LOADING', true)
    try {
      // Obtener inventario y productos
      const [inventoryResponse, productsResponse] = await Promise.all([
        apiClient.getInventory(),
        apiClient.getProducts()
      ])
      
      console.log('Respuesta de inventario:', inventoryResponse.data)
      console.log('Respuesta de productos:', productsResponse.data)

      // Crear un mapa de productos por ID para búsqueda rápida
      const productsMap = productsResponse.data.reduce((acc, product) => {
        acc[product.id] = product
        return acc
      }, {})
      
      console.log('Mapa de productos:', productsMap)

      // Combinar datos de inventario con productos
      const combinedInventory = inventoryResponse.data.map(item => {
        console.log('Procesando item de inventario:', item)
        const fullProduct = productsMap[item.product.id]
        console.log('Producto completo encontrado:', fullProduct)
        
        if (!fullProduct) {
          console.warn('No se encontró el producto completo para el ID:', item.product.id)
        }

        return {
          id: item.id,
          // Propiedades planas para la tabla
          product_id: item.product.id,
          location_id: item.location.id,
          product_sku: item.product.sku,
          product_name: item.product.name,
          location_code: item.location.code,
          // Propiedades originales
          sku: item.product.sku,
          name: item.product.name,
          description: item.product.description,
          category: fullProduct?.category || item.product.category || 'Sin categoría',
          current_stock: item.quantity,
          min_stock: fullProduct?.min_stock || 0,
          location: item.location.code,
          abc_category: fullProduct?.abc_category || 'C',
          status: fullProduct?.status || 'active',
          unit_of_measure: fullProduct?.unit_of_measure || item.product.unit_of_measure || 'unidad',
          created_at: item.created_at,
          updated_at: item.updated_at,
          // Campos adicionales del producto
          barcode: fullProduct?.barcode,
          reorder_point: fullProduct?.reorder_point || 0,
          dimensions: fullProduct?.dimensions,
          weight: fullProduct?.weight,
          // Mantener objetos originales para referencia si es necesario
          product: item.product,
          location: item.location,
          quantity: item.quantity,
          lot_number: item.lot_number ? item.lot_number : 'N/A'
        }
      })

      console.log('Inventario combinado:', combinedInventory)
      commit('SET_INVENTORY', combinedInventory)
    } catch (error) {
      console.error('Error en fetchInventory:', error)
    } finally {
      commit('SET_LOADING', false)
    }
  },
  fetchInventoryItem({ commit }, id) {
    commit('SET_LOADING', true)
    apiClient.getInventoryItem(id)
      .then(response => {
        commit('SET_INVENTORY_ITEM', response.data)
        commit('SET_LOADING', false)
      })
      .catch(error => {
        commit('SET_LOADING', false)
        console.error('Error fetching inventory item:', error)
      })
  },
  updateInventoryItem({ commit }, { id, adjustment }) {
    commit('SET_LOADING', true)
    // Se asume que para actualizar el inventario se usa el endpoint de ajuste
    return apiClient.adjustInventory(id, adjustment)
      .then(response => {
        // Aquí puedes actualizar la vista con la nueva información recibida del backend
        commit('UPDATE_INVENTORY_ITEM', response.data)
        commit('SET_LOADING', false)
        return response.data
      })
      .catch(error => {
        commit('SET_LOADING', false)
        return Promise.reject(error)
      })
  },
  async getInventoryStats({ commit }) {
    commit('SET_LOADING', true)
    try {
      const response = await apiClient.getInventoryStats()
      commit('SET_INVENTORY_STATS', response.data)
      return response.data
    } catch (error) {
      console.error('Error fetching inventory stats:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },
  async getRecentActivities({ commit }) {
    commit('SET_LOADING', true)
    try {
      const response = await apiClient.getRecentActivities()
      commit('SET_RECENT_ACTIVITIES', response.data)
      return response.data
    } catch (error) {
      console.error('Error fetching recent activities:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },
  async getLowStockProducts({ commit }) {
    commit('SET_LOADING', true)
    try {
      const response = await apiClient.getLowStockProducts()
      commit('SET_LOW_STOCK_PRODUCTS', response.data)
      return response.data
    } catch (error) {
      console.error('Error fetching low stock products:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },
  async adjustInventory({ commit }, adjustment) {
    commit('SET_LOADING', true)
    try {
      const adjustmentData = {
        adjustment_type: adjustment.quantity > 0 ? 'set' : 'set',
        quantity: adjustment.quantity,
        reason: adjustment.reason || 'Ajuste manual'
      }
      
      const response = await apiClient.adjustInventory(adjustment.id, adjustmentData)
      
      // Actualizar el item en el estado
      if (response.data.inventory) {
        commit('UPDATE_INVENTORY_ITEM', response.data.inventory)
      }
      
      return response.data
    } catch (error) {
      console.error('Error adjusting inventory:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  }
}

const mutations = {
  SET_LOADING(state, status) {
    state.loading = status
  },
  SET_INVENTORY(state, inventory) {
    console.log('SET_INVENTORY mutation recibió:', inventory)
    state.inventory = inventory
    console.log('Estado actualizado del inventario:', state.inventory)
  },
  SET_INVENTORY_ITEM(state, inventoryItem) {
    state.inventoryItem = inventoryItem
  },
  UPDATE_INVENTORY_ITEM(state, updatedItem) {
    const index = state.inventory.findIndex(item => item.id === updatedItem.id)
    if (index !== -1) {
      state.inventory.splice(index, 1, updatedItem)
    }
    if (state.inventoryItem && state.inventoryItem.id === updatedItem.id) {
      state.inventoryItem = updatedItem
    }
  },
  SET_INVENTORY_STATS(state, stats) {
    state.stats = stats
  },
  SET_RECENT_ACTIVITIES(state, activities) {
    state.recentActivities = activities
  },
  SET_LOW_STOCK_PRODUCTS(state, products) {
    state.lowStockProducts = products
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
